import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import {
  readPrevBrandAnswerAnswers,
  writePrevBrandAnswerAnswers,
  type PrevBrandAnswerAnswers,
} from '@/lib/prevBrandAnswerStorage';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import { clearOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const hintClass = 'm-0 text-left text-[14px] leading-tight text-black/80 sm:text-[15px]';

type ElaboratedKey =
  | 'taste'
  | 'price'
  | 'productFeatures'
  | 'brandCredibility'
  | 'peersSmoking';

function ElaboratedOption({
  id,
  label,
  checked,
  elaboration,
  hint,
  onToggle,
  onElaborationChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  elaboration: string;
  hint: string;
  onToggle: () => void;
  onElaborationChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className={surveyChoiceInputClass}
        />
        <span className={labelClass}>{label}</span>
      </label>
      <p className={hintClass}>{hint}</p>
      <input
        type="text"
        value={elaboration}
        onChange={(e) => onElaborationChange(e.target.value)}
        onFocus={() => {
          if (!checked) onToggle();
        }}
        className={inputClass}
      />
    </div>
  );
}

export default function PrevBrandAnswer() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<PrevBrandAnswerAnswers>(() => readPrevBrandAnswerAnswers());
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    writePrevBrandAnswerAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/ldproductnow" replace />;
  if (!isPrevBrandComplete()) return <Navigate to="/survey/prevbrand" replace />;

  const toggleElaboratedReason = (key: ElaboratedKey) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: {
        checked: !prev[key].checked,
        elaboration: prev[key].elaboration,
      },
    }));
    setWarning(null);
  };

  const setElaboration = (key: ElaboratedKey, elaboration: string) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: { checked: true, elaboration },
    }));
    setWarning(null);
  };

  const handleOtherReasonChange = (otherReason: string) => {
    setAnswers((prev) => ({
      ...prev,
      otherReason,
    }));
    setWarning(null);
  };

  const handleContinue = () => {
    const trimmedOther = answers.otherReason.trim();
    const hasReason =
      answers.taste.checked ||
      answers.price.checked ||
      answers.productFeatures.checked ||
      answers.brandCredibility.checked ||
      answers.peersSmoking.checked ||
      trimmedOther.length > 0;

    if (!hasReason) {
      setWarning(t.prevBrandAnswer.reasonRequiredWarning);
      return;
    }
    if (answers.taste.checked && !answers.taste.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.tasteElaborationWarning);
      return;
    }
    if (answers.price.checked && !answers.price.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.priceElaborationWarning);
      return;
    }
    if (answers.productFeatures.checked && !answers.productFeatures.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.productFeaturesElaborationWarning);
      return;
    }
    if (answers.brandCredibility.checked && !answers.brandCredibility.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.brandCredibilityElaborationWarning);
      return;
    }
    if (answers.peersSmoking.checked && !answers.peersSmoking.elaboration.trim()) {
      setWarning(t.prevBrandAnswer.peersSmokingElaborationWarning);
      return;
    }
    if (answers.smokedOtherProduct !== 'yes' && answers.smokedOtherProduct !== 'no') {
      setWarning(t.prevBrandAnswer.otherProductWarning);
      return;
    }

    writePrevBrandAnswerAnswers({ ...answers, otherReason: trimmedOther });
    setWarning(null);
    navigate(answers.smokedOtherProduct === 'no' ? '/whynotld' : '/survey/prevbrand-other');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswer.whyQuestion}</p>

                <ElaboratedOption
                  id="reason-taste"
                  label={t.prevBrandAnswer.taste}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.taste.checked}
                  elaboration={answers.taste.elaboration}
                  onToggle={() => toggleElaboratedReason('taste')}
                  onElaborationChange={(elaboration) => setElaboration('taste', elaboration)}
                />

                <ElaboratedOption
                  id="reason-price"
                  label={t.prevBrandAnswer.price}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.price.checked}
                  elaboration={answers.price.elaboration}
                  onToggle={() => toggleElaboratedReason('price')}
                  onElaborationChange={(elaboration) => setElaboration('price', elaboration)}
                />

                <ElaboratedOption
                  id="reason-product-features"
                  label={t.prevBrandAnswer.productFeatures}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.productFeatures.checked}
                  elaboration={answers.productFeatures.elaboration}
                  onToggle={() => toggleElaboratedReason('productFeatures')}
                  onElaborationChange={(elaboration) => setElaboration('productFeatures', elaboration)}
                />

                <ElaboratedOption
                  id="reason-brand-credibility"
                  label={t.prevBrandAnswer.brandCredibility}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.brandCredibility.checked}
                  elaboration={answers.brandCredibility.elaboration}
                  onToggle={() => toggleElaboratedReason('brandCredibility')}
                  onElaborationChange={(elaboration) => setElaboration('brandCredibility', elaboration)}
                />

                <ElaboratedOption
                  id="reason-peers"
                  label={t.prevBrandAnswer.peersSmoking}
                  hint={t.prevBrandAnswer.elaborateHint}
                  checked={answers.peersSmoking.checked}
                  elaboration={answers.peersSmoking.elaboration}
                  onToggle={() => toggleElaboratedReason('peersSmoking')}
                  onElaborationChange={(elaboration) => setElaboration('peersSmoking', elaboration)}
                />

                <div className="flex flex-col gap-1">
                  <label htmlFor="reason-other" className={labelClass}>
                    {t.prevBrandAnswer.otherReasons}
                  </label>
                  <input
                    id="reason-other"
                    type="text"
                    value={answers.otherReason}
                    onChange={(e) => handleOtherReasonChange(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-1.5">
                <p id="smoked-other-product-question" className={surveyQuestionTextClass}>
                  {t.prevBrandAnswer.otherProductQuestion}
                </p>
                <select
                  id="smoked-other-product"
                  value={answers.smokedOtherProduct}
                  onChange={(e) => {
                    const smokedOtherProduct = e.target.value as PrevBrandAnswerAnswers['smokedOtherProduct'];
                    if (smokedOtherProduct === 'no') {
                      clearOtherPrevBrandAnswers();
                      clearOtherPrevBrandAnswerAnswers();
                    }
                    setAnswers((prev) => ({
                      ...prev,
                      smokedOtherProduct,
                    }));
                    setWarning(null);
                  }}
                  className={inputClass}
                  aria-labelledby="smoked-other-product-question"
                >
                  <option value="" disabled>
                    {t.prevBrandAnswer.selectYesNo}
                  </option>
                  <option value="yes">{t.prevBrandAnswer.yes}</option>
                  <option value="no">{t.prevBrandAnswer.no}</option>
                </select>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-2 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" onClick={handleContinue}>
                {t.prevBrandAnswer.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
